import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LoginPage from "./pages/LoginPage";

// Layout Components
import Sidebar from "./components/layout/Sidebar";

// Inbox Components
import ConversationList from "./components/inbox/ConversationList";
import ConversationView from "./components/inbox/ConversationView";
import CustomerPanel from "./components/inbox/CustomerPanel";

// Other Views
import AgentsView from "./components/agents/AgentsView";
import AutomationBuilder from "./components/automation/AutomationBuilder";
import AnalyticsView from "./components/analytics/AnalyticsView";
import SettingsView from "./components/settings/SettingsView";
import ClaimsView from "./components/claims/ClaimsView";

// API
import { getConversations, getConversation, getUsers, getSettings, getWhatsAppSenders } from "./services/api";
import { Loader2 } from "lucide-react";

// Notifications hook
import useNotifications from "./hooks/useNotifications";

const baseChannels = [
  { id: 'all', name: 'All Channels', icon: 'Inbox', count: 0 },
  { id: 'email', name: 'Email', icon: 'Mail', count: 0 },
  { id: 'whatsapp', name: 'WhatsApp', icon: 'MessageCircle', count: 0, hasSubChannels: true },
  { id: 'facebook', name: 'Facebook', icon: 'Facebook', count: 0 }
];

function AppContent() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [activeView, setActiveView] = useState('inbox');
  const [activeChannel, setActiveChannel] = useState('all');
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState(baseChannels);
  const [whatsappSenders, setWhatsappSenders] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({ total: 0, limit: 50, skip: 0, hasMore: false });
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Notifications
  const { notifyNewMessage, requestPermission } = useNotifications();
  const prevConversationsRef = useRef([]);
  const notificationSettingsRef = useRef({ push_notifications: true, sound_alerts: true });

  // Request notification permission on mount
  useEffect(() => {
    if (isAuthenticated) {
      requestPermission();
      // Load notification settings
      getSettings().then(settings => {
        notificationSettingsRef.current = settings.notifications || { push_notifications: true, sound_alerts: true };
      }).catch(() => {});
      
      // Load WhatsApp senders for sub-channels
      getWhatsAppSenders().then(senders => {
        setWhatsappSenders(senders);
      }).catch(() => {});
    }
  }, [isAuthenticated, requestPermission]);

  const handleSelectConversation = useCallback(async (conv) => {
    try {
      const full = await getConversation(conv.id);
      setSelectedConversation(full);
    } catch {
      setSelectedConversation(conv);
    }
  }, []);

  const handleUpdate = useCallback(async () => {
    const [convResponse, usersData] = await Promise.all([
      getConversations(activeChannel),
      getUsers()
    ]);
    
    // Handle new paginated response format
    const convData = convResponse.conversations || convResponse;
    
    // Check for new messages and trigger notifications
    if (notificationSettingsRef.current.push_notifications && prevConversationsRef.current.length > 0) {
      for (const conv of convData) {
        const prevConv = prevConversationsRef.current.find(c => c.id === conv.id);
        // New conversation or new message (check by updated_at)
        if (!prevConv) {
          // New conversation
          notifyNewMessage(
            conv.customer?.name || 'Unknown',
            conv.last_message,
            conv.channel
          );
        } else if (conv.updated_at !== prevConv.updated_at && conv.unread) {
          // Updated conversation with new message
          notifyNewMessage(
            conv.customer?.name || 'Unknown',
            conv.last_message,
            conv.channel
          );
        }
      }
    }
    prevConversationsRef.current = convData;
    
    setConversations(convData);
    setAgents(usersData);
    
    // Update pagination info
    if (convResponse.total !== undefined) {
      setPagination({
        total: convResponse.total,
        limit: convResponse.limit,
        skip: convResponse.skip,
        hasMore: convResponse.has_more
      });
    }
    
    // Update channel counts including WhatsApp sub-channels
    if (activeChannel === 'all' || activeChannel === 'whatsapp' || activeChannel.startsWith('whatsapp:')) {
      const openConversations = convData.filter(c => c.status !== 'resolved');
      
      // Build channels with WhatsApp sub-channels
      const updatedChannels = baseChannels.map(ch => {
        if (ch.id === 'whatsapp') {
          // WhatsApp main channel count
          const waConvs = openConversations.filter(c => c.channel === 'whatsapp');
          return {
            ...ch,
            count: waConvs.length,
            subChannels: whatsappSenders.map(sender => ({
              id: `whatsapp:${sender.phone_number}`,
              name: sender.name,
              phone: sender.phone_number,
              count: waConvs.filter(c => c.channel_id === sender.phone_number).length
            }))
          };
        }
        return {
          ...ch,
          count: ch.id === 'all' ? openConversations.length : openConversations.filter(c => c.channel === ch.id).length
        };
      });
      setChannels(updatedChannels);
    }
    
    // Re-fetch the selected conversation to get updated status/data
    if (selectedConversation) {
      try {
        const refreshed = await getConversation(selectedConversation.id);
        setSelectedConversation(refreshed);
      } catch { /* conversation may have been deleted */ }
    }
  }, [activeChannel, selectedConversation, notifyNewMessage, whatsappSenders]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Parse channel filter - handle whatsapp sub-channels
      let channelFilter = activeChannel;
      let channelIdFilter = null;
      
      if (activeChannel.startsWith('whatsapp:')) {
        channelFilter = 'whatsapp';
        channelIdFilter = activeChannel.replace('whatsapp:', '');
      }
      
      const [convResponse, usersData] = await Promise.all([
        getConversations(channelFilter, channelIdFilter),
        getUsers()
      ]);
      
      // Handle new paginated response format
      const convData = convResponse.conversations || convResponse;
      
      setConversations(convData);
      setAgents(usersData);
      
      // Update pagination info
      if (convResponse.total !== undefined) {
        setPagination({
          total: convResponse.total,
          limit: convResponse.limit,
          skip: convResponse.skip,
          hasMore: convResponse.has_more
        });
      }
      
      // Update channel counts including WhatsApp sub-channels
      const allConvsResponse = await getConversations('all');
      const allConvs = allConvsResponse.conversations || allConvsResponse;
      const openConversations = allConvs.filter(c => c.status !== 'resolved');
      
      const updatedChannels = baseChannels.map(ch => {
        if (ch.id === 'whatsapp') {
          const waConvs = openConversations.filter(c => c.channel === 'whatsapp');
          return {
            ...ch,
            count: waConvs.length,
            subChannels: whatsappSenders.map(sender => ({
              id: `whatsapp:${sender.phone_number}`,
              name: sender.name,
              phone: sender.phone_number,
              count: waConvs.filter(c => c.channel_id === sender.phone_number).length
            }))
          };
        }
        return {
          ...ch,
          count: ch.id === 'all' ? openConversations.length : openConversations.filter(c => c.channel === ch.id).length
        };
      });
      setChannels(updatedChannels);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeChannel, whatsappSenders]);
  
  // Load more conversations
  const loadMoreConversations = useCallback(async () => {
    if (!pagination.hasMore || loadingMore) return;
    
    setLoadingMore(true);
    try {
      let channelFilter = activeChannel;
      let channelIdFilter = null;
      
      if (activeChannel.startsWith('whatsapp:')) {
        channelFilter = 'whatsapp';
        channelIdFilter = activeChannel.replace('whatsapp:', '');
      }
      
      const convResponse = await getConversations(
        channelFilter, 
        channelIdFilter, 
        pagination.limit, 
        pagination.skip + pagination.limit
      );
      
      const newConvs = convResponse.conversations || convResponse;
      
      setConversations(prev => [...prev, ...newConvs]);
      setPagination({
        total: convResponse.total,
        limit: convResponse.limit,
        skip: convResponse.skip,
        hasMore: convResponse.has_more
      });
    } catch (error) {
      console.error('Failed to load more conversations:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [activeChannel, pagination, loadingMore]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  // Auto-refresh conversations every 10 seconds for real-time updates
  useEffect(() => {
    if (!isAuthenticated || activeView !== 'inbox') return;
    
    const pollInterval = setInterval(() => {
      handleUpdate();
    }, 10000); // 10 seconds
    
    return () => clearInterval(pollInterval);
  }, [isAuthenticated, activeView, handleUpdate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Render current view
  const renderView = () => {
    switch (activeView) {
      case 'inbox':
        return (
          <div className="flex flex-1 h-screen overflow-hidden">
            <ConversationList
              conversations={conversations}
              selectedConversation={selectedConversation}
              setSelectedConversation={handleSelectConversation}
              agents={agents}
              loading={loading}
              onSync={loadData}
              pagination={pagination}
              onLoadMore={loadMoreConversations}
              loadingMore={loadingMore}
            />
            <ConversationView
              conversation={selectedConversation}
              agents={agents}
              onUpdate={handleUpdate}
            />
            <CustomerPanel conversation={selectedConversation} onUpdate={handleUpdate} />
          </div>
        );
      
      case 'agents':
        return <AgentsView />;
      
      case 'automation':
        return <AutomationBuilder />;
      
      case 'claims':
        return <ClaimsView />;
      
      case 'analytics':
        return <AnalyticsView />;
      
      case 'settings':
        return <SettingsView />;
      
      default:
        return null;
    }
  };

  return (
    <div className="App flex bg-gray-50">
      <Sidebar
        channels={channels}
        activeChannel={activeChannel}
        setActiveChannel={setActiveChannel}
        activeView={activeView}
        setActiveView={setActiveView}
      />
      <main className="ml-64 flex-1 flex">
        {renderView()}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
